import { useState, useEffect, ReactElement } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import PurchaseHistoryDrawer from "../customers/PurchaseHistoryDrawer";
import AllergyModal, { AllergyData } from "./AllergyModal";
import PatchTestModal, { PatchTestData } from "./PatchTestModal";
import NotesModal, { NoteData } from "./NotesModal";
import AddCustomerModal from "./AddCustomerModal";
import PosPaymentStep from "../PosPaymentStep";
import ConsultationCard from "../ConsultationCard";
import api from "../../services/api";
import { getConsultationsByDate } from "../../services/consultationform";
import GlobalLoader from "../ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import { useAuth } from "../../auth/AuthContext";

import {
  X,
  ChevronDown,
  Calendar,
  Tag,
  User,
  FileText,
  ShoppingCart,
  Cake,
  Clock,
  Edit2,
  Scissors as ScissorsIcon,
} from "lucide-react";

type Customer = {
  id: number;
  name: string;
  email: string;
  mobile?: string;
  created?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  birthday?: string;
  year?: string;
  gender?: string;
  pronouns?: string;
  clientSource?: string;
  referredBy?: string;
  preferredLanguage?: string;
  country?: string;
  occupation?: string;
  additionalEmail?: string;
  additionalPhone?: string;
};

interface Props {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
}

type MenuItem = {
  label: string;
  icon: ReactElement;
  badge?: number;
  submenu?: string[];
};

interface Appointment {
  id: number;
  title: string;
  dateLabel: string;
  location: string;
  provider: string;
  duration: string;
  price: string;
  status: string;
  amountToPay?: number;
  amountPaid?: number;
  pendingAmount?: number;
  currency?: string;
}

interface Purchase {
  id: number;
  title: string;
  dateLabel: string;
  location: string;
  provider: string;
  items: string;
  price: string;
  status: string;
}

interface Consultation {
  id: string;
  clientName: string;
  consultationType: string;
  time: string;
  services: string;
}

// Severity Icon Component
const SeverityIcon = ({ severity }: { severity: string }) => {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "mild":
        return "#22c55e";
      case "moderate":
        return "#f97316";
      case "severe":
        return "#ec4899";
      case "fatal":
        return "#000000";
      default:
        return "#9ca3af";
    }
  };

  const color = getSeverityColor(severity);

  return (
    <div className="flex-shrink-0">
      <div className="w-24 h-24 border border-gray-200 rounded-3xl bg-white flex flex-col items-center justify-center gap-2">
        <svg width="48" height="48" viewBox="0 0 48 48">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((row) =>
            [0, 1, 2, 3, 4, 5, 6, 7, 8].map((col) => {
              const x = 6 + col * 4;
              const y = 6 + row * 4;
              const centerX = 24;
              const centerY = 24;
              const distance = Math.sqrt(
                Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)
              );

              if (distance <= 18) {
                return (
                  <circle
                    key={`${row}-${col}`}
                    cx={x}
                    cy={y}
                    r="1.5"
                    fill={color}
                  />
                );
              }
              return null;
            })
          )}
        </svg>
        <span className="text-sm font-medium text-gray-700 capitalize">
          {severity}
        </span>
      </div>
    </div>
  );
};

// Patch Test Status Icon
const PatchTestStatusIcon = ({ status }: { status: string }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "#f97316";
      case "passed":
        return "#22c55e";
      case "failed":
        return "#ec4899";
      default:
        return "#9ca3af";
    }
  };

  const color = getStatusColor(status);

  return (
    <div className="flex-shrink-0">
      <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <svg width="32" height="32" viewBox="0 0 32 32">
          {[0, 1, 2].map((row) =>
            [0, 1, 2].map((col) => {
              const x = 10 + col * 6;
              const y = 10 + row * 6;
              return (
                <circle
                  key={`${row}-${col}`}
                  cx={x}
                  cy={y}
                  r="2"
                  fill={color}
                />
              );
            })
          )}
        </svg>
      </div>
    </div>
  );
};

export default function CustomerDetailsDrawer({
  customer,
  isOpen,
  onClose,
}: Props) {
  const { user: loggedInUser } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>("Overview");
  const [isVisible, setIsVisible] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(
    null
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [allergyModalOpen, setAllergyModalOpen] = useState(false);
  const [editCustomerModalOpen, setEditCustomerModalOpen] = useState(false);
  const [allergies, setAllergies] = useState<AllergyData[]>([]);
  const [editingAllergy, setEditingAllergy] = useState<AllergyData | null>(null);

  // Patch Test State
  const [patchTestModalOpen, setPatchTestModalOpen] = useState(false);
  const [patchTests, setPatchTests] = useState<PatchTestData[]>([]);
  const [editingPatchTest, setEditingPatchTest] = useState<PatchTestData | null>(null);

  // Notes State
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notes, setNotes] = useState<NoteData[]>([]);
  const [editingNote, setEditingNote] = useState<NoteData | null>(null);
  const [activeNotesTab, setActiveNotesTab] = useState<"client" | "appointment">("client");
  const [paymentStepOpen, setPaymentStepOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [tip, setTip] = useState(0);
  const [galleryImages, setGalleryImages] = useState<Array<{ id: number; url: string; name: string; selected: boolean }>>([]);
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);

  // Loading states for API operations
  const [savingAllergy, setSavingAllergy] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [savingPatchTest, setSavingPatchTest] = useState(false);
  const [deletingPatchTest, setDeletingPatchTest] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [deletingImages, setDeletingImages] = useState(false);

  // Appointments State
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [appointmentsError, setAppointmentsError] = useState<string | null>(null);

  // Purchase History State
  const [purchaseHistory, setPurchaseHistory] = useState<Purchase[]>([]);
  const [loadingPurchaseHistory, setLoadingPurchaseHistory] = useState(false);
  const [purchaseHistoryError, setPurchaseHistoryError] = useState<string | null>(null);

  // Payment/Spending Data State
  const [paymentData, setPaymentData] = useState({
    productAmount: 0,
    serviceSpent: 0,
    totalBookingsRefund: 0,
    totalSpent: 0,
    stats: null as any,
  });

  // Consultations State
  const [customerIncompleteConsultations, setCustomerIncompleteConsultations] = useState<any[]>([]);
  const [customerCompleteConsultations, setCustomerCompleteConsultations] = useState<any[]>([]);
  const [loadingConsultations, setLoadingConsultations] = useState(false);

  // Fetch Appointments and Purchase History
  useEffect(() => {
    if (isOpen && customer) {
      fetchCustomerData();
    }
  }, [isOpen, customer]);

  const fetchCustomerData = async () => {
    setLoadingAppointments(true);
    setLoadingPurchaseHistory(true);
    setAppointmentsError(null);
    setPurchaseHistoryError(null);

    if (!customer?.id) return;

    try {
      // Parallel fetch for all data
      const [
        detailsRes,
        allergiesRes,
        patchTestsRes,
        notesRes,
        imagesRes
      ] = await Promise.all([
        api.get(`/admin/customers/${customer.id}`),
        api.get(`/admin/allergies?customer_id=${customer.id}`).catch(() => ({ data: { data: [] } })),
        api.get(`/admin/patch-tests?customer_id=${customer.id}`).catch(() => ({ data: { data: [] } })),
        api.get(`/admin/notes?customer_id=${customer.id}`).catch(() => ({ data: { data: [] } })),
        api.get(`/admin/customers/images?customer_id=${customer.id}`).catch(() => ({ data: { data: [] } }))
      ]);

      const data = detailsRes.data.data;
      const customerInfo = data.customer;

      if (data) {
        // ... (appointments processing) ...

        // ... (purchase history processing) ...

        // ... (notes processing) ...

        // ... (allergies processing) ...

        // ... (patch tests processing) ...

        // Process Images from separate API call
        if (imagesRes.data && (Array.isArray(imagesRes.data.images) || Array.isArray(imagesRes.data.data) || Array.isArray(imagesRes.data))) {
          const imagesDataRaw = Array.isArray(imagesRes.data.images)
            ? imagesRes.data.images
            : (Array.isArray(imagesRes.data.data)
              ? imagesRes.data.data
              : (Array.isArray(imagesRes.data) ? imagesRes.data : []));

          const transformedImages = imagesDataRaw.map((image: any) => ({
            id: image.id,
            url: `https://salonify.co.uk/storage/${image.image_path}`,
            name: image.path || `Image ${image.id}`,
            selected: false,
          }));
          setGalleryImages(transformedImages);
        } else if (customerInfo.images && Array.isArray(customerInfo.images)) {
          // Fallback to customer object if present (legacy?)
          const transformedImages = customerInfo.images.map((image: any) => ({
            id: image.id,
            url: `https://salonify.co.uk/storage/${image.image_path}`,
            name: image.path || `Image ${image.id}`,
            selected: false,
          }));
          setGalleryImages(transformedImages);
        } else {
          setGalleryImages([]);
        }
        // Process Appointments from customer_bookings (nested in customer object)
        if (customerInfo && customerInfo.customer_bookings) {
          const transformedAppointments: Appointment[] = customerInfo.customer_bookings.map((booking: any) => {
            const currencySymbol = booking.company?.currency?.currency_symbol || '';
            const currencyCode = booking.company?.currency?.currency_code || 'GBP';
            const cleanCurrencyCode = currencyCode.replace(/\d+/g, '');
            const displayCurrency = currencySymbol || (cleanCurrencyCode === 'GBP' ? '£' : cleanCurrencyCode);

            // Determine title from items if available
            const title = booking.items && booking.items.length > 0
              ? booking.items.map((i: any) => i.name).join(", ")
              : "Appointment";

            return {
              id: booking.id,
              title: title,
              dateLabel: new Date(booking.date_time).toLocaleString('en-GB', {
                weekday: 'short',
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
              }),
              location: booking.location?.name || "No Location",
              provider: booking.user?.name || booking.employee?.name || "Staff Member",
              duration: booking.duration ? `${booking.duration}min` : "30min",
              price: booking.formated_amount_to_pay || `${displayCurrency} ${booking.amount_to_pay}`,
              status: ((booking.status || "") as string).charAt(0).toUpperCase() + ((booking.status || "") as string).slice(1),
              amountToPay: booking.amount_to_pay,
              amountPaid: booking.amount_paid || 0,
              pendingAmount: (booking.amount_to_pay || 0) - (booking.amount_paid || 0),
              currency: displayCurrency,
            };
          });

          setAppointments(transformedAppointments);

          // Process Purchase History (items into history)
          const transformedPurchases: Purchase[] = [];

          // If items are not present in the customer_bookings snippet provided by user, 
          // we might just map the booking itself as a purchase for now.
          // However, ideally checking booking.items.
          customerInfo.customer_bookings.forEach((booking: any) => {
            const currencySymbol = booking.company?.currency?.currency_symbol || '';
            const currencyCode = booking.company?.currency?.currency_code || 'GBP';
            const cleanCurrencyCode = currencyCode.replace(/\d+/g, '');
            const displayCurrency = currencySymbol || (cleanCurrencyCode === 'GBP' ? '£' : cleanCurrencyCode);

            if (booking.items && Array.isArray(booking.items) && booking.items.length > 0) {
              booking.items.forEach((item: any) => {
                transformedPurchases.push({
                  id: item.id || `${booking.id}-${Math.random()}`, // fallback id
                  title: item.product?.name || item.name || "Service",
                  dateLabel: new Date(booking.date_time).toLocaleString('en-GB', {
                    weekday: 'short',
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  }),
                  location: booking.location?.name || "No Location",
                  provider: booking.user?.name || booking.employee?.name || "Staff Member",
                  items: `${item.quantity || 1}x ${item.product?.name || item.name || "Item"}`,
                  price: `${displayCurrency} ${Number(item.total || item.price || 0).toFixed(2)}`,
                  status: booking.payment_status === "paid" || booking.payment_status === "completed" ? "Completed" : "Pending",
                });
              });
            } else {
              // Fallback if no items: showing the booking as a single purchase entry
              transformedPurchases.push({
                id: booking.id,
                title: "Booking",
                dateLabel: new Date(booking.date_time).toLocaleString('en-GB', {
                  weekday: 'short',
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                }),
                location: booking.location?.name || "No Location",
                provider: booking.user?.name || booking.employee?.name || "Staff Member",
                items: "Services",
                price: booking.formated_amount_to_pay || `${displayCurrency} ${Number(booking.amount_to_pay).toFixed(2)}`,
                status: booking.payment_status === "paid" || booking.payment_status === "completed" ? "Completed" : "Pending",
              });
            }
          });

          // Remove duplicates based on unique id
          const uniquePurchases = transformedPurchases.filter((purchase, index, self) =>
            index === self.findIndex((p) => p.id === purchase.id)
          );

          setPurchaseHistory(uniquePurchases);
        } else {
          setAppointments([]);
          setPurchaseHistory([]);
        }

        // Process Notes from separate API call
        if (notesRes.data && (notesRes.data.success || Array.isArray(notesRes.data.data))) {
          const notesDataRaw = Array.isArray(notesRes.data.data) ? notesRes.data.data : [];
          const transformedNotes: NoteData[] = notesDataRaw.map((note: any) => ({
            id: note.id,
            content: note.text,
            date: new Date(note.created_at).toLocaleString('en-GB', {
              weekday: 'short',
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit'
            }),
            author: note.user?.name || "Staff Member",
          }));
          setNotes(transformedNotes);
        } else {
          setNotes([]);
        }

        // Process Allergies from separate API call
        if (allergiesRes.data && (Array.isArray(allergiesRes.data.allergies) || Array.isArray(allergiesRes.data))) {
          const allergiesDataRaw = Array.isArray(allergiesRes.data.allergies)
            ? allergiesRes.data.allergies
            : (Array.isArray(allergiesRes.data) ? allergiesRes.data : []);

          const transformedAllergies: AllergyData[] = allergiesDataRaw.map((allergy: any) => ({
            id: allergy.id,
            type: allergy.type || "non-drug",
            name: allergy.name,
            reaction: allergy.reaction,
            severity: allergy.severity?.toLowerCase() || "mild",
            note: allergy.description,
          }));
          setAllergies(transformedAllergies);
        } else {
          setAllergies([]);
        }

        // Process Patch Tests from separate API call
        if (patchTestsRes.data && (Array.isArray(patchTestsRes.data.patch_tests) || Array.isArray(patchTestsRes.data))) {
          const patchTestsDataRaw = Array.isArray(patchTestsRes.data.patch_tests)
            ? patchTestsRes.data.patch_tests
            : (Array.isArray(patchTestsRes.data) ? patchTestsRes.data : []);

          const transformedPatchTests: PatchTestData[] = patchTestsDataRaw.map((patchTest: any) => ({
            id: patchTest.id,
            title: patchTest.title,
            tested: new Date(patchTest.created_at).toLocaleString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            }),
            testedBy: patchTest.tested_by || "Staff Member",
            status: patchTest.status?.toLowerCase() || "pending",
            description: patchTest.description,
            expiresOn: patchTest.date && !patchTest.date.startsWith("-0001") ? new Date(patchTest.date).toLocaleString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            }) : "N/A",
          }));
          setPatchTests(transformedPatchTests);
        } else {
          setPatchTests([]);
        }

        // Process Images (Still checking customer object, or separate call if needed later)
        if (customerInfo.images && Array.isArray(customerInfo.images)) {
          const transformedImages = customerInfo.images.map((image: any) => ({
            id: image.id,
            url: `https://salonify.co.uk/storage/${image.image_path}`,
            name: image.path || `Image ${image.id}`,
            selected: false,
          }));
          setGalleryImages(transformedImages);
        } else {
          setGalleryImages([]);
        }

        // Spending data is available in the main detail object
        setPaymentData({
          productAmount: data.productAmount || 0,
          serviceSpent: data.serviceSpent || 0,
          totalBookingsRefund: data.totalBookingsRefund || 0,
          totalSpent: data.totalSpent || 0,
          stats: data.stats || null,
        });
      }

      // Fetch Consultations
      if (customer?.id) {
        setLoadingConsultations(true);
        try {
          const consultationData = await getConsultationsByDate();
          const formatTime = (isoString: string) => {
            const date = new Date(isoString);
            return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
          };

          // Filter consultations for the current customer (checking both user_id and user object if available)
          const customerIncomplete = consultationData.incomplete_forms?.filter((item: any) =>
            item.user_id === customer.id || item.user?.id === customer.id
          ) || [];

          const customerCompleted = consultationData.completed_forms?.filter((item: any) =>
            item.user_id === customer.id || item.user?.id === customer.id
          ) || [];

          const incomplete = customerIncomplete.map((item: any) => ({
            id: String(item.id),
            clientName: item.user?.name || customer?.name || "Client",
            consultationType: item.form?.title || "Untitled Form",
            time: formatTime(item.created_at),
            services: item.form?.description || "No description",
          }));

          const completed = customerCompleted.map((item: any) => ({
            id: String(item.id),
            clientName: item.user?.name || customer?.name || "Client",
            consultationType: item.form?.title || "Untitled Form",
            time: formatTime(item.created_at),
            services: item.form?.description || "No description",
          }));

          setCustomerIncompleteConsultations(incomplete);
          setCustomerCompleteConsultations(completed);
        } catch (error: any) {
          console.error("Error fetching customer consultations:", error);
          toast.error("Failed to load consultations");
        } finally {
          setLoadingConsultations(false);
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to fetch customer data";
      setAppointmentsError(errorMessage);
      setPurchaseHistoryError(errorMessage);
      toast.error(errorMessage);
      console.error("Error fetching customer data:", err);
    } finally {
      setLoadingAppointments(false);
      setLoadingPurchaseHistory(false);
    }
  };

  useEffect(() => {
    if (isOpen && customer) {
      setIsVisible(true);
      setIsClosing(false);
    }
  }, [isOpen, customer]);

  const handleOpenPurchaseDrawer = (purchase: Purchase) => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setSelectedPurchase(purchase);
      setDrawerOpen(true);
    }, 300);
  };

  const handleClosePurchaseDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => {
      setIsVisible(true);
      setIsClosing(false);
    }, 100);
  };

  const handleClose = () => {
    onClose();
  };

  const handleSaveAllergy = async (allergyData: AllergyData) => {
    setSavingAllergy(true);
    try {
      if (editingAllergy) {
        // Update existing allergy
        const response = await api.put(`/admin/allergies/${editingAllergy.id}`, {
          name: allergyData.name,
          reaction: allergyData.reaction || null,
          severity: allergyData.severity || null,
          description: allergyData.note || null,
          type: allergyData.type,
          customer_id: customer?.id,
        });

        if (response.data.success) {
          setAllergies(
            allergies.map((a) => (a.id === editingAllergy.id ? { ...allergyData, id: a.id } : a))
          );
          toast.success("Allergy updated successfully");
          setEditingAllergy(null);
          setAllergyModalOpen(false); // Close modal after successful save
        }
      } else {
        // Create new allergy
        const response = await api.post('/admin/allergies', {
          name: allergyData.name,
          reaction: allergyData.reaction || null,
          severity: allergyData.severity || null,
          description: allergyData.note || null,
          type: allergyData.type,
          customer_id: customer?.id,
        });

        if (response.data.success) {
          const newAllergy = { ...allergyData, id: response.data.data?.id || Date.now() };
          setAllergies([...allergies, newAllergy]);
          toast.success("Allergy added successfully");
          setAllergyModalOpen(false); // Close modal after successful save
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error saving allergy';
      toast.error(errorMessage);
      console.error('Error saving allergy:', error);
    } finally {
      setSavingAllergy(false);
    }
  };

  const handleEditAllergy = (allergy: AllergyData) => {
    setEditingAllergy(allergy);
    setAllergyModalOpen(true);
  };

  const handleCloseAllergyModal = () => {
    setAllergyModalOpen(false);
    setEditingAllergy(null);
  };

  // Patch Test Handlers
  const handleSavePatchTest = async (patchTestData: PatchTestData) => {
    setSavingPatchTest(true);
    try {
      if (editingPatchTest) {
        // Update existing patch test
        const response = await api.put(`/admin/patch-tests/${editingPatchTest.id}`, {
          title: patchTestData.title,
          description: patchTestData.description || null,
          status: patchTestData.status,
          tested_by: patchTestData.testedBy,
          date: patchTestData.expiresOn,
          customer_id: customer?.id,
        });

        if (response.data.success) {
          setPatchTests(
            patchTests.map((p) => (p.id === editingPatchTest.id ? { ...patchTestData, id: p.id } : p))
          );
          toast.success("Patch test updated successfully");
          setEditingPatchTest(null);
          setPatchTestModalOpen(false); // Close modal after successful save
        }
      } else {
        // Create new patch test
        const response = await api.post('/admin/patch-tests', {
          title: patchTestData.title,
          description: patchTestData.description || null,
          status: patchTestData.status,
          tested_by: patchTestData.testedBy,
          date: patchTestData.expiresOn,
          customer_id: customer?.id,
        });

        if (response.data.success) {
          const newPatchTest = { ...patchTestData, id: response.data.data?.id || Date.now() };
          setPatchTests([...patchTests, newPatchTest]);
          toast.success("Patch test added successfully");
          setPatchTestModalOpen(false); // Close modal after successful save
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error saving patch test';
      toast.error(errorMessage);
      console.error('Error saving patch test:', error);
    } finally {
      setSavingPatchTest(false);
    }
  };

  const handleEditPatchTest = (patchTest: PatchTestData) => {
    setEditingPatchTest(patchTest);
    setPatchTestModalOpen(true);
  };

  const handleDeletePatchTest = async (id: number) => {
    setDeletingPatchTest(true);
    try {
      const response = await api.delete(`/admin/patch-tests/${id}`);

      if (response.data.success) {
        setPatchTests(patchTests.filter((p) => p.id !== id));
        toast.success("Patch test deleted successfully");
        setPatchTestModalOpen(false); // Close modal after successful delete
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error deleting patch test';
      toast.error(errorMessage);
      console.error('Error deleting patch test:', error);
    } finally {
      setDeletingPatchTest(false);
    }
  };

  const handleClosePatchTestModal = () => {
    setPatchTestModalOpen(false);
    setEditingPatchTest(null);
  };

  // Notes Handlers
  const handleSaveNote = async (noteData: NoteData) => {
    setSavingNote(true);
    try {
      if (editingNote) {
        // Update existing note
        const response = await api.put(`/admin/notes/${editingNote.id}`, {
          text: noteData.content,
          customer_id: customer?.id,
        });

        if (response.data.success) {
          setNotes(
            notes.map((n) => (n.id === editingNote.id ? { ...noteData, id: n.id } : n))
          );
          toast.success("Note updated successfully");
          setEditingNote(null);
          setNotesModalOpen(false); // Close modal after successful save
        }
      } else {
        // Create new note
        const response = await api.post('/admin/notes', {
          text: noteData.content,
          customer_id: customer?.id,
        });

        if (response.data.success || response.status === 200 || response.status === 201) {
          const responseData = response.data.data || response.data;
          const newNote = {
            ...noteData,
            id: responseData.id || Date.now(),
            // Ensure author is correct from response if available, else standard fallback
            author: responseData.user?.name || loggedInUser?.name || "Staff Member"
          };
          setNotes([...notes, newNote]);
          toast.success("Note added successfully");
          setNotesModalOpen(false);
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error saving note';
      toast.error(errorMessage);
      console.error('Error saving note:', error);
    } finally {
      setSavingNote(false);
    }
  };

  const handleEditNote = (note: NoteData) => {
    setEditingNote(note);
    setNotesModalOpen(true);
  };

  const handleCloseNotesModal = () => {
    setNotesModalOpen(false);
    setEditingNote(null);
  };

  const handleSaveCustomer = (data: any) => {
    console.log("Customer data saved:", data);
    // Handle customer update logic here
  };

  const handleOpenPaymentStep = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setPaymentStepOpen(true);
    }, 300);
  };

  const handleClosePaymentStep = () => {
    setPaymentStepOpen(false);
    setTimeout(() => {
      setIsVisible(true);
      setIsClosing(false);
    }, 100);
  };

  const handleGoToFinalPayment = () => {
    // Handle navigation to final payment if needed
    console.log("Go to final payment");
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || !customer) return;

    setUploadingImages(true);
    try {
      const formData = new FormData();
      formData.append('customer_id', customer.id.toString());

      Array.from(files).forEach((file) => {
        if (file.type.startsWith("image/")) {
          formData.append('images[]', file);
        }
      });

      const response = await api.post('/admin/customers/upload-images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        toast.success("Images uploaded successfully");

        // If response contains updated images list, use it directly
        const responseData = response.data.images || response.data.data;
        if (responseData && Array.isArray(responseData)) {
          const transformedImages = responseData.map((image: any) => ({
            id: image.id,
            url: `https://salonify.co.uk/storage/${image.image_path}`,
            name: image.path || `Image ${image.id}`,
            selected: false,
          }));
          setGalleryImages(transformedImages);
        }

        // Refresh customer data to ensure everything is in sync
        await fetchCustomerData();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error uploading images';
      toast.error(errorMessage);
      console.error('Error uploading images:', error);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleImageUpload(e.dataTransfer.files);
  };

  const handleImageSelect = (id: number) => {
    setGalleryImages((prev) =>
      prev.map((img) => (img.id === id ? { ...img, selected: !img.selected } : img))
    );
  };

  const handleSelectAll = () => {
    const allSelected = galleryImages.every((img) => img.selected);
    setGalleryImages((prev) =>
      prev.map((img) => ({ ...img, selected: !allSelected }))
    );
  };

  const handleDeleteSelected = async () => {
    const selectedIds = galleryImages.filter((img) => img.selected).map((img) => img.id);

    if (selectedIds.length === 0) return;

    setDeletingImages(true);
    try {
      const response = await api.post('/admin/customers/delete-images', {
        image_ids: selectedIds,
      });

      if (response.data.success) {
        toast.success("Images deleted successfully");
        // Remove deleted images from state
        setGalleryImages((prev) => prev.filter((img) => !selectedIds.includes(img.id)));
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error deleting images';
      toast.error(errorMessage);
      console.error('Error deleting images:', error);
    } finally {
      setDeletingImages(false);
    }
  };

  const handleDeleteImage = async (id: number) => {
    setDeletingImages(true);
    try {
      const response = await api.post('/admin/customers/delete-images', {
        image_ids: [id],
      });

      if (response.data.success) {
        setGalleryImages((prev) => prev.filter((img) => img.id !== id));
        toast.success("Image deleted successfully");
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error deleting image';
      toast.error(errorMessage);
    } finally {
      setDeletingImages(false);
    }
  };

  const handleActionsClick = (action: string) => {
    setActionsDropdownOpen(false);
    if (action === "Allergies") {
      setActiveSection("Allergies");
      setExpandedMenu("Documents");
      setAllergyModalOpen(true);
    } else if (action === "Notes") {
      setActiveSection("Notes");
      setExpandedMenu("Documents");
      setNotesModalOpen(true);
    } else if (action === "Patch Test") {
      setActiveSection("Patch Tests");
      setExpandedMenu("Documents");
      setPatchTestModalOpen(true);
    }
  };

  const handleBookNow = () => {
    // Create a default appointment for booking
    const defaultAppointment: Appointment = {
      id: Date.now(),
      title: "New Appointment",
      dateLabel: new Date().toLocaleDateString('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      }),
      location: "Beauty Parlor",
      provider: "Staff Member",
      duration: "30min",
      price: "£0",
      status: "New",
    };

    setSelectedAppointment(defaultAppointment);
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setPaymentStepOpen(true);
    }, 300);
  };



  const menu: MenuItem[] = [
    { label: "Overview", icon: <User className="w-5 h-5" /> },
    { label: "Appointments", icon: <Calendar className="w-5 h-5" />, badge: appointments.length },
    {
      label: "History",
      icon: <ShoppingCart className="w-5 h-5" />,
      badge: purchaseHistory.length,
    },
    { label: "Consultations", icon: <FileText className="w-5 h-5" /> },
    {
      label: "Client details",
      icon: <FileText className="w-5 h-5" />,
    },
    {
      label: "Documents",
      icon: <FileText className="w-5 h-5" />,
      submenu: ["Notes", "Allergies", "Patch Tests"],
    },
    { label: "Gallery", icon: <Tag className="w-5 h-5" /> },
    { label: "Payments", icon: <Tag className="w-5 h-5" /> },
  ];

  const toggleSubmenu = (label: string) => {
    setExpandedMenu(expandedMenu === label ? null : label);
  };

  const handleMenuClick = (label: string, hasSubmenu?: boolean) => {
    if (hasSubmenu) {
      toggleSubmenu(label);
      if (expandedMenu === label) setActiveSection(label);
    } else {
      setActiveSection(label);
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[100] flex justify-end">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
              className="absolute inset-0 bg-black/40 backdrop-blur-[2px] z-[99] transition-opacity duration-300"
              aria-hidden="true"
            />

            {/* Drawer Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="relative w-full lg:w-[85vw] max-w-5xl h-screen bg-white shadow-[-10px_0_30px_rgba(0,0,0,0.05)] border-l border-gray-100 z-[100] flex origin-right"
            >
              {/* Standardized Close Button (Matches QuickPaymentPanel style) */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleClose}
                className="absolute lg:-left-14 right-4 top-4 lg:right-auto lg:top-8 w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-[-4px_0_12px_rgba(0,0,0,0.1)] lg:shadow-[-4px_0_12px_rgba(0,0,0,0.1)] hover:bg-[#402B69] hover:text-white transition-all z-[110] group"
                aria-label="Close"
              >
                <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
              </motion.button>

              {/* Drawer Content */}
              <section className="relative z-50 w-full h-screen bg-white shadow-2xl flex flex-col lg:flex-row overflow-hidden">
                {/* Sidebar Menu with Profile */}
                <nav className="w-full lg:w-[280px] border-b lg:border-b-0 lg:border-r border-gray-200 overflow-y-auto lg:overflow-auto flex-shrink-0">
                  {/* Profile Section */}
                  <div className="px-5 lg:px-6 py-6 lg:py-6 border-b border-gray-200">
                    <div className="flex items-center gap-4 mb-4 mt-8 lg:mt-0">
                      <div className="w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 text-lg lg:text-xl font-bold border border-purple-200 shadow-sm">
                        {customer?.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 truncate">
                          {customer?.name}
                        </h3>
                        <p className="text-gray-500 text-xs lg:text-sm truncate">{customer?.email}</p>
                      </div>
                    </div>

                    <div className="flex flex-row gap-2 mb-4">
                      <div className="relative flex-1">
                        <button
                          onClick={() => setActionsDropdownOpen(!actionsDropdownOpen)}
                          className="flex items-center justify-center gap-1 font-bold border border-gray-200 bg-gray-50/50 rounded-xl px-4 lg:px-5 py-2.5 text-xs lg:text-sm text-gray-700 hover:bg-gray-100 w-full transition-colors"
                        >
                          Actions <ChevronDown className="w-3 h-3" />
                        </button>

                        {/* Dropdown Menu */}
                        {actionsDropdownOpen && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setActionsDropdownOpen(false)}
                            />
                            <div className="absolute top-full left-0 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-xl z-20 overflow-hidden ring-1 ring-black/5">
                              <button
                                onClick={() => handleActionsClick("Allergies")}
                                className="w-full text-left px-4 py-3 text-xs font-semibold text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition"
                              >
                                Add Allergy
                              </button>
                              <button
                                onClick={() => handleActionsClick("Notes")}
                                className="w-full text-left px-4 py-3 text-xs font-semibold text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition border-t border-gray-50"
                              >
                                Add Note
                              </button>
                              <button
                                onClick={() => handleActionsClick("Patch Test")}
                                className="w-full text-left px-4 py-3 text-xs font-semibold text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition border-t border-gray-50"
                              >
                                Add Patch Test
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                      <button
                        onClick={handleBookNow}
                        className="flex items-center justify-center bg-[#885ABB] hover:bg-[#724a9d] text-white px-4 lg:px-5 py-2.5 text-xs lg:text-sm font-bold transition-all rounded-xl flex-1 whitespace-nowrap shadow-sm active:scale-95"
                      >
                        Book now
                      </button>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-xs lg:text-sm text-gray-500 font-medium">
                        <Clock className="w-4 h-4 text-purple-400" />
                        <span>Created {customer?.created ?? "—"}</span>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items - Scrollable area for mobile/desktop */}
                  <div className="p-3 lg:p-4 bg-gray-50/30 lg:bg-transparent">
                    <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 no-scrollbar">
                      {menu.map((item) => {
                        const isActive = activeSection === item.label;
                        const isExpanded = expandedMenu === item.label;
                        return (
                          <div key={item.label} className="flex-shrink-0 lg:flex-shrink">
                            <button
                              onClick={() =>
                                handleMenuClick(item.label, !!item.submenu)
                              }
                              className={`flex items-center justify-between gap-3 lg:w-full px-4 lg:px-3 py-2.5 lg:py-2 rounded-xl text-xs lg:text-sm font-bold transition-all border whitespace-nowrap lg:whitespace-normal ${isActive
                                ? "bg-[#885ABB] text-white border-[#885ABB] shadow-md shadow-purple-200"
                                : "bg-white lg:bg-transparent text-gray-600 hover:bg-gray-100 border-gray-100 lg:border-transparent hover:border-gray-200"
                                }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className={isActive ? "text-white" : "text-[#885ABB]"}>{item.icon}</span>
                                <span>{item.label}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                {item.badge !== undefined && item.badge > 0 && (
                                  <span className={`min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-[10px] font-black ${isActive ? 'bg-white text-[#885ABB]' : 'bg-purple-100 text-[#885ABB]'}`}>
                                    {item.badge}
                                  </span>
                                )}
                                {item.submenu && (
                                  <ChevronDown
                                    className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-180" : ""
                                      }`}
                                  />
                                )}
                              </div>
                            </button>

                            {item.submenu && isExpanded && (
                              <div className="hidden lg:block mt-1 ml-8 space-y-1">
                                {item.submenu.map((subItem) => (
                                  <button
                                    key={subItem}
                                    onClick={() => setActiveSection(subItem)}
                                    className={`block w-full text-left px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeSection === subItem
                                      ? "bg-purple-50 text-[#885ABB]"
                                      : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                                      }`}
                                  >
                                    {subItem}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </nav>

                {/* Main Content */}
                <main className="flex-1 p-5 lg:p-8 overflow-y-auto bg-gray-50/30">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
                    <div>
                      <h2 className="text-xl lg:text-2xl font-black text-gray-900 tracking-tight">
                        {activeSection}
                      </h2>
                      <p className="text-gray-600 text-sm mt-1">
                        {activeSection === "Overview"
                          ? "Customer overview and summary"
                          : activeSection === "Appointments"
                            ? "View and manage appointments"
                            : activeSection === "History"
                              ? "View history and transactions"
                              : activeSection === "Consultations"
                                ? "View customer consultations"
                                : activeSection === "Client details"
                                  ? "Manage client personal information"
                                  : activeSection === "Allergies"
                                    ? "Manage customer allergies"
                                    : activeSection === "Patch Tests"
                                      ? "Manage patch tests"
                                      : activeSection === "Notes"
                                        ? "View and manage notes"
                                        : "Details"}
                      </p>
                    </div>
                    {activeSection === "Consultations" && (
                      <button
                        onClick={() => navigate("/form-templates")}
                        className="px-6 py-2 bg-[#885ABB] text-white rounded-lg text-sm font-medium hover:opacity-90 transition"
                      >
                        Form Templates
                      </button>
                    )}
                    {activeSection === "Client details" && (
                      <button
                        onClick={() => setEditCustomerModalOpen(true)}
                        className="flex text-white items-center gap-2 px-4 py-2 bg-[#885ABB] border border-gray-300 rounded-lg text-sm font-medium hover:bg-purple-800"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit
                      </button>
                    )}
                    {(activeSection === "Allergies" || activeSection === "Patch Tests" || activeSection === "Notes") && (
                      <button
                        onClick={() => {
                          if (activeSection === "Allergies") {
                            setAllergyModalOpen(true);
                          } else if (activeSection === "Patch Tests") {
                            setPatchTestModalOpen(true);
                          } else if (activeSection === "Notes") {
                            setNotesModalOpen(true);
                          }
                        }}
                        className="px-4 py-2 text-white bg-[#885ABB] border border-gray-300 rounded-lg text-sm font-medium hover:bg-[#6f47a2]"
                      >
                        Add
                      </button>
                    )}
                  </div>

                  {/* Client Details Section */}
                  {activeSection === "Client details" && (
                    <section className="space-y-8">
                      {/* Profile Section */}
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Profile</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-1">Full name</p>
                            <p className="text-gray-900">{customer?.name || "-"}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-1">Email</p>
                            <p className="text-gray-900">{customer?.email || "-"}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-1">Phone number</p>
                            <p className="text-gray-900">{customer?.phone || customer?.mobile || "-"}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-1">Date of birth</p>
                            <p className="text-gray-900">{customer?.birthday && customer?.year ? `${customer.birthday} ${customer.year}` : "-"}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-1">Gender</p>
                            <p className="text-gray-900 capitalize">{customer?.gender || "-"}</p>
                          </div>

                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-1">Joined</p>
                            <p className="text-gray-900">{customer?.created || "-"}</p>
                          </div>
                        </div>
                      </div>
                    </section>
                  )}

                  {/* Overview Section */}
                  {activeSection === "Overview" && (
                    <section className="space-y-6">
                      {/* Quick Stats Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Allergies Card */}
                        <div className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-200 rounded-xl p-5 shadow-sm">
                          <div className="flex items-center justify-between mb-2">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                            </div>
                            <span className="text-2xl font-bold text-red-700">{allergies.length}</span>
                          </div>
                          <p className="text-sm font-medium text-gray-700">Known Allergies</p>
                        </div>

                        {/* Patch Tests Card */}
                        <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-5 shadow-sm">
                          <div className="flex items-center justify-between mb-2">
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <span className="text-2xl font-bold text-purple-700">{patchTests.length}</span>
                          </div>
                          <p className="text-sm font-medium text-gray-700">Patch Tests</p>
                        </div>

                        {/* Notes Card */}
                        <div className="bg-gradient-to-br from-green-50 to-teal-50 border border-green-200 rounded-xl p-5 shadow-sm">
                          <div className="flex items-center justify-between mb-2">
                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </div>
                            <span className="text-2xl font-bold text-green-700">{notes.length}</span>
                          </div>
                          <p className="text-sm font-medium text-gray-700">Clinical Notes</p>
                        </div>
                      </div>

                      {/* Allergies List */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-xl font-bold text-gray-900">Allergies</h3>
                          <button
                            onClick={() => setAllergyModalOpen(true)}
                            className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                          >
                            + Add Allergy
                          </button>
                        </div>
                        <div className="space-y-3">
                          {allergies.map((allergy) => (
                            <div
                              key={allergy.id}
                              onClick={() => handleEditAllergy(allergy)}
                              className="bg-white border border-gray-200 rounded-xl p-4 hover:border-purple-300 hover:shadow-md cursor-pointer transition flex items-center gap-4"
                            >
                              {allergy.severity && (
                                <div className="flex-shrink-0">
                                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${allergy.severity === 'mild' ? '#22c55e' : allergy.severity === 'moderate' ? '#f97316' : allergy.severity === 'severe' ? '#ec4899' : '#9ca3af'}20` }}>
                                    <svg width="24" height="24" viewBox="0 0 24 24">
                                      {[0, 1, 2, 3, 4].map((row) =>
                                        [0, 1, 2, 3, 4].map((col) => {
                                          const x = 4 + col * 4;
                                          const y = 4 + row * 4;
                                          const centerX = 12;
                                          const centerY = 12;
                                          const distance = Math.sqrt(
                                            Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)
                                          );
                                          if (distance <= 9) {
                                            return (
                                              <circle
                                                key={`${row}-${col}`}
                                                cx={x}
                                                cy={y}
                                                r="1"
                                                fill={allergy.severity === 'mild' ? '#22c55e' : allergy.severity === 'moderate' ? '#f97316' : allergy.severity === 'severe' ? '#ec4899' : '#9ca3af'}
                                              />
                                            );
                                          }
                                          return null;
                                        })
                                      )}
                                    </svg>
                                  </div>
                                </div>
                              )}
                              <div className="flex-1">
                                <h5 className="font-semibold text-gray-900 mb-1">{allergy.name}</h5>
                                <p className="text-sm text-gray-600">
                                  {allergy.severity && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize" style={{
                                      backgroundColor: `${allergy.severity === 'mild' ? '#22c55e' : allergy.severity === 'moderate' ? '#f97316' : allergy.severity === 'severe' ? '#ec4899' : '#9ca3af'}20`,
                                      color: allergy.severity === 'mild' ? '#16a34a' : allergy.severity === 'moderate' ? '#ea580c' : allergy.severity === 'severe' ? '#db2777' : '#6b7280'
                                    }}>
                                      {allergy.severity} severity
                                    </span>
                                  )}
                                  {allergy.reaction && (
                                    <span className="ml-2 text-gray-600">{allergy.reaction}</span>
                                  )}
                                </p>
                              </div>
                              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          ))}
                          {allergies.length === 0 && (
                            <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                              <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <p className="font-medium mb-1">No allergies recorded</p>
                              <p className="text-sm text-gray-400">Click "Add Allergy" to record customer allergies</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Recent Patch Tests */}
                      {patchTests.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-gray-900">Recent Patch Tests</h3>
                            <button
                              onClick={() => {
                                setActiveSection("Patch Tests");
                                setExpandedMenu("Documents");
                              }}
                              className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                            >
                              View All
                            </button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {patchTests.slice(0, 4).map((test) => (
                              <div
                                key={test.id}
                                className="bg-white border border-gray-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-sm cursor-pointer transition"
                                onClick={() => handleEditPatchTest(test)}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <h5 className="font-semibold text-gray-900 text-sm">{test.title}</h5>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${test.status === 'passed' ? 'bg-green-100 text-green-700' :
                                    test.status === 'failed' ? 'bg-red-100 text-red-700' :
                                      'bg-orange-100 text-orange-700'
                                    }`}>
                                    {test.status}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                  <span className="font-bold">Tested By:</span>
                                  <span>{test.testedBy}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Recent Clinical Notes */}
                      {notes.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-gray-900">Clinical Notes</h3>
                            <button
                              onClick={() => {
                                setActiveSection("Notes");
                                setExpandedMenu("Documents");
                              }}
                              className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                            >
                              View All
                            </button>
                          </div>
                          <div className="space-y-3">
                            {notes.slice(0, 3).map((note) => (
                              <div
                                key={note.id}
                                className="bg-white border border-gray-200 rounded-xl p-4 hover:border-purple-300 hover:shadow-md cursor-pointer transition"
                                onClick={() => handleEditNote(note)}
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <span className="text-xs font-bold text-gray-500 uppercase">{note.date}</span>
                                  <span className="text-xs text-purple-600 font-medium">{note.author}</span>
                                </div>
                                <p className="text-sm text-gray-700 line-clamp-2">{note.content}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Client Quick Info */}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Client Information</h3>
                        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                            <div>
                              <p className="text-xs text-gray-500 uppercase font-bold mb-1">Phone</p>
                              <p className="text-sm text-gray-900 font-medium">{customer?.phone || customer?.mobile || "N/A"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase font-bold mb-1">Email</p>
                              <p className="text-sm text-gray-900 font-medium truncate">{customer?.email || "N/A"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase font-bold mb-1">Source</p>
                              <p className="text-sm text-gray-900 font-medium">{customer?.clientSource || "N/A"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase font-bold mb-1">Referred By</p>
                              <p className="text-sm text-gray-900 font-medium">{customer?.referredBy || "N/A"}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Summary */}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Summary</h3>
                        <div className="bg-white border border-gray-200 rounded-lg">
                          {/* Total Sales */}
                          <div className="p-4 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-700">Total sales</span>
                            </div>
                            <div className="text-2xl font-bold text-gray-900 mt-1">£{Number(paymentData.totalSpent).toFixed(2)}</div>
                          </div>

                          {/* Statistics Grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-200">
                            {/* Appointments */}
                            <div className="p-4">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-gray-700">Appointments</span>
                              </div>
                              <div className="text-2xl font-bold text-gray-900">{appointments.length}</div>
                            </div>

                            {/* Total Purchases */}
                            <div className="p-4">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-gray-700">Purchases</span>

                              </div>
                              <div className="text-2xl font-bold text-gray-900">{purchaseHistory.length}</div>
                            </div>
                          </div>

                          {/* Bottom Grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-200 border-t border-gray-200">
                            <div className="p-4">
                              <div className="flex items-center gap-2 mb-1">
                                <ScissorsIcon className="w-4 h-4 text-purple-600" />
                                <span className="text-gray-700 text-sm">Services</span>
                              </div>
                              <div className="text-xl font-bold text-gray-900">£{Number(paymentData.serviceSpent).toFixed(2)}</div>
                            </div>
                            <div className="p-4">
                              <div className="flex items-center gap-2 mb-1">
                                <ShoppingCart className="w-4 h-4 text-blue-600" />
                                <span className="text-gray-700 text-sm">Products</span>
                              </div>
                              <div className="text-xl font-bold text-gray-900">£{Number(paymentData.productAmount).toFixed(2)}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Appointment Stats (if available) */}
                      {paymentData.stats && (
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-4">Appointment Stats</h3>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-white border border-gray-200 rounded-lg p-3">
                              <p className="text-xs text-gray-500 uppercase font-bold">Completed</p>
                              <p className="text-lg font-bold text-green-600">{paymentData.stats.completed || 0}</p>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-lg p-3">
                              <p className="text-xs text-gray-500 uppercase font-bold">Canceled</p>
                              <p className="text-lg font-bold text-red-600">{paymentData.stats.canceled || 0}</p>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-lg p-3">
                              <p className="text-xs text-gray-500 uppercase font-bold">Pending</p>
                              <p className="text-lg font-bold text-orange-600">{paymentData.stats.pending || 0}</p>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-lg p-3">
                              <p className="text-xs text-gray-500 uppercase font-bold">No Show</p>
                              <p className="text-lg font-bold text-gray-600">{paymentData.stats.no_show || 0}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </section>
                  )}
                  {/* Appointments */}
                  {activeSection === "Appointments" && (
                    <section className="space-y-6">
                      {loadingAppointments ? (
                        <div className="py-12">
                          <GlobalLoader description="Loading appointments..." compensateSidebar={false} />
                        </div>
                      ) : appointmentsError ? (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-red-600">{appointmentsError}</p>
                        </div>
                      ) : appointments.length === 0 ? (
                        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                          <p>No appointments found</p>
                        </div>
                      ) : (
                        <>
                          <div className="relative">
                            <div className="absolute left-8 top-0 bottom-0 w-px bg-gray-300" />
                            {appointments.map((a) => (
                              <div
                                key={a.id}
                                className="pl-12 pr-4 mb-6 cursor-pointer"
                                onClick={() => handleOpenPaymentStep(a)}
                              >
                                <div className="flex items-start gap-4">
                                  <div className="w-8 h-8 rounded-full bg-[#885ABB] text-white flex items-center justify-center -ml-12 shadow-lg border border-gray-700 relative z-10">
                                    <Calendar className="w-4 h-4" />
                                  </div>
                                  <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 shadow-lg hover:border-purple-300 transition">
                                    <div className="flex items-start justify-between">
                                      <div>
                                        <h5 className="text-lg font-semibold text-gray-900">
                                          {a.title}
                                        </h5>
                                        <p className="text-gray-600 text-sm mt-1">
                                          {a.dateLabel} · {a.location}
                                        </p>
                                      </div>
                                      <div className="text-right">
                                        <span className="inline-block bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-sm border border-blue-200">
                                          {a.status}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Payment Details */}
                                    {a.amountToPay !== undefined && (
                                      <div className="mt-4 pt-4 border-t border-gray-200">
                                        <div className="grid grid-cols-3 gap-4 text-sm">
                                          <div>
                                            <p className="text-gray-500 text-sm mb-1">Amount to Pay</p>
                                            <p className="font-semibold text-gray-900">
                                              {a.currency}{" "}{Number(a.amountToPay).toFixed(2)}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-gray-500 text-sm mb-1">Amount Paid</p>
                                            <p className="font-semibold text-green-600">
                                              {a.currency}{" "}{Number(a.amountPaid).toFixed(2)}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-gray-500 text-sm mb-1">Pending</p>
                                            <p className={`font-semibold ${a.pendingAmount && a.pendingAmount > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
                                              {a.currency}{" "}{Number(a.pendingAmount).toFixed(2)}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </section>
                  )}

                  {/* Consultations Section */}
                  {activeSection === "Consultations" && (
                    <div className="space-y-8">
                      {/* Incomplete Consultations */}
                      <section className="mb-8">
                        <div className="mb-4">
                          <h3 className="text-xl font-semibold text-purple-700">Incomplete</h3>
                        </div>
                        <div className="space-y-4">
                          {loadingConsultations ? (
                            <div className="py-8">
                              <GlobalLoader description="Loading incomplete consultations..." compensateSidebar={false} />
                            </div>
                          ) : customerIncompleteConsultations.length > 0 ? (
                            customerIncompleteConsultations.map((consultation) => (
                              <ConsultationCard
                                key={consultation.id}
                                clientName={consultation.clientName}
                                consultationType={consultation.consultationType}
                                time={consultation.time}
                                services={consultation.services}
                                onClick={() => {
                                  navigate(`/consultationform/${consultation.id}`);
                                }}
                              />
                            ))
                          ) : (
                            <p className="text-sm text-gray-500">No incomplete consultations found.</p>
                          )}
                        </div>
                      </section>

                      {/* Complete Consultations */}
                      <section className="mb-8">
                        <div className="mb-4">
                          <h3 className="text-xl font-semibold text-purple-700">Complete</h3>
                        </div>
                        <div className="space-y-4">
                          {loadingConsultations ? (
                            <div className="py-8">
                              <GlobalLoader description="Loading completed consultations..." compensateSidebar={false} />
                            </div>
                          ) : customerCompleteConsultations.length > 0 ? (
                            customerCompleteConsultations.map((consultation) => (
                              <ConsultationCard
                                key={consultation.id}
                                clientName={consultation.clientName}
                                consultationType={consultation.consultationType}
                                time={consultation.time}
                                services={consultation.services}
                                onClick={() => {
                                  navigate(`/consultationform/${consultation.id}`);
                                }}
                              />
                            ))
                          ) : (
                            <p className="text-sm text-gray-500">No completed consultations found.</p>
                          )}
                        </div>
                      </section>
                    </div>
                  )}

                  {/* History */}
                  {activeSection === "History" && (
                    <section className="space-y-6">
                      {loadingPurchaseHistory ? (
                        <div className="py-12">
                          <GlobalLoader description="Loading purchase history..." compensateSidebar={false} />
                        </div>
                      ) : purchaseHistoryError ? (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-red-600">{purchaseHistoryError}</p>
                        </div>
                      ) : purchaseHistory.length === 0 ? (
                        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                          <p>No purchase history found</p>
                        </div>
                      ) : (
                        <>
                          <h4 className="text-gray-600 text-base">Purchase History</h4>
                          <div className="relative">
                            <div className="absolute left-8 top-0 bottom-0 w-px bg-gray-300" />
                            {purchaseHistory.map((purchase) => (
                              <div
                                key={purchase.id}
                                className="pl-12 pr-4 mb-6 cursor-pointer"
                                onClick={() => handleOpenPurchaseDrawer(purchase)}
                              >
                                <div className="flex items-start gap-4">
                                  <div className="w-8 h-8 rounded-full bg-[#885ABB] text-white flex items-center justify-center -ml-12 shadow-lg border border-gray-700 relative z-10">
                                    <ShoppingCart className="w-4 h-4" />
                                  </div>
                                  <div className="flex-1 bg-white border border-gray-200 rounded-lg p-5 shadow-md hover:border-purple-300 transition">
                                    <div className="flex items-center justify-between">
                                      <h5 className="font-semibold text-gray-900">
                                        {purchase.title}
                                      </h5>
                                      <span className={`text-sm font-medium ${purchase.status === 'Completed' ? 'text-green-600' : 'text-orange-600'
                                        }`}>
                                        {purchase.status}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1">
                                      {purchase.dateLabel}
                                    </p>
                                    <p className="mt-2 text-sm text-gray-800">
                                      {purchase.items}
                                    </p>
                                    <div className="border-t mt-3 pt-3 flex justify-between font-semibold text-gray-900">
                                      <span>Total</span>
                                      <span>{purchase.price}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </section>
                  )}

                  {/* Notes Section */}
                  {activeSection === "Notes" && (
                    <section className="space-y-6">
                      {/* Tabs */}
                      <div className="flex gap-4">
                        <button
                          onClick={() => setActiveNotesTab("client")}
                          className={`px-6 py-2 rounded-full text-sm font-medium transition ${activeNotesTab === "client"
                            ? "bg-[#885ABB] text-white"
                            : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                            }`}
                        >
                          Client notes
                          {notes.length > 0 && (
                            <span className={`ml-2 px-2 py-0.5 rounded-full text-sm ${activeNotesTab === "client"
                              ? "bg-white text-black"
                              : "bg-gray-100 text-gray-600"
                              }`}>
                              {notes.length}
                            </span>
                          )}
                        </button>

                      </div>

                      {/* Notes List */}
                      {activeNotesTab === "client" && (
                        <div className="space-y-4">
                          {notes.map((note) => (
                            <div
                              key={note.id}
                              onClick={() => handleEditNote(note)}
                              className="bg-white border border-gray-200 rounded-lg p-4 hover:border-purple-300 cursor-pointer transition"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <p className="text-sm text-gray-600">
                                  {note.date} · {note.author}
                                </p>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                  className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                                >
                                  Actions
                                </button>
                              </div>
                              <p className="text-gray-900">{note.content}</p>
                            </div>
                          ))}
                          {notes.length === 0 && (
                            <div className="text-center py-12 text-gray-500">
                              <p>No notes recorded</p>
                              <p className="text-sm mt-2">
                                Click "Add" to create a note
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {activeNotesTab === "appointment" && (
                        <div className="text-center py-12 text-gray-500">
                          <p>No appointment notes</p>
                        </div>
                      )}
                    </section>
                  )}

                  {/* Allergies Section */}
                  {activeSection === "Allergies" && (
                    <section className="space-y-4">
                      {allergies.map((allergy) => (
                        <div
                          key={allergy.id}
                          onClick={() => handleEditAllergy(allergy)}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:border-purple-300 cursor-pointer transition"
                        >
                          <div className="flex items-start gap-4">
                            {allergy.severity && (
                              <SeverityIcon severity={allergy.severity} />
                            )}
                            <div className="flex-1">
                              <h5 className="font-semibold text-gray-900">
                                {allergy.name}
                              </h5>
                              <p className="text-sm text-gray-600 mt-1">
                                {allergy.severity && (
                                  <span className="capitalize">
                                    {allergy.severity} allergy
                                  </span>
                                )}
                                {allergy.severity && allergy.reaction && " · "}
                                {allergy.reaction}
                              </p>
                              {allergy.note && (
                                <p className="text-sm text-gray-500 mt-2">
                                  {allergy.note}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {allergies.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                          <p>No allergies recorded</p>
                          <p className="text-sm mt-2">
                            Click "Add" to record customer allergies
                          </p>
                        </div>
                      )}
                    </section>
                  )}

                  {/* Patch Tests Section */}
                  {activeSection === "Patch Tests" && (
                    <section className="space-y-4">
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-1">Active</h4>
                      </div>
                      {patchTests.map((patchTest) => (
                        <div
                          key={patchTest.id}
                          onClick={() => handleEditPatchTest(patchTest)}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:border-purple-300 cursor-pointer transition"
                        >
                          <div className="flex items-start gap-4">
                            <PatchTestStatusIcon status={patchTest.status} />
                            <div className="flex-1">
                              <h5 className="font-semibold text-gray-900">
                                {patchTest.title}
                              </h5>
                              <p className="text-sm text-gray-600 mt-1">
                                Expires {patchTest.expiresOn} · <span className="capitalize">{patchTest.status}</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                      {patchTests.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                          <p>No patch tests recorded</p>
                          <p className="text-sm mt-2">
                            Click "Add" to record patch tests
                          </p>
                        </div>
                      )}
                    </section>
                  )}

                  {/* Gallery Section */}
                  {activeSection === "Gallery" && (
                    <section className="space-y-6">
                      {/* Upload Area */}
                      <div
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-dashed border-purple-300 rounded-xl p-12 text-center hover:border-purple-400 transition-colors"
                      >
                        <div className="flex flex-col items-center gap-4">
                          <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center">
                            <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">
                              Drag & drop your images here
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                              or click the button below to browse
                            </p>
                            <label className="inline-block">
                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(e.target.files)}
                                disabled={uploadingImages}
                              />
                              <span className="inline-flex items-center gap-2 px-6 py-3 bg-[#885ABB] text-white rounded-lg font-medium hover:bg-[#6f47a2] cursor-pointer transition disabled:opacity-50 disabled:cursor-not-allowed">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                </svg>
                                {uploadingImages ? "Uploading..." : "Choose Images"}
                              </span>
                            </label>
                          </div>
                          <p className="text-sm text-gray-500">
                            Supported formats: JPG, PNG, GIF, WebP (Max 10MB each)
                          </p>
                        </div>
                      </div>

                      {/* Uploaded Images Grid */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900">
                            Uploaded Images
                            <span className="ml-2 text-sm font-normal text-gray-500">
                              ({galleryImages.length} {galleryImages.length === 1 ? "image" : "images"})
                            </span>
                          </h3>
                          {galleryImages.length > 0 && (
                            <div className="flex gap-2">
                              <button
                                onClick={handleSelectAll}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                              >
                                {galleryImages.every((img) => img.selected) ? "Deselect All" : "Select All"}
                              </button>
                              <button
                                onClick={handleDeleteSelected}
                                disabled={!galleryImages.some((img) => img.selected) || deletingImages}
                                className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {deletingImages ? "Deleting..." : "Delete Selected"}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Empty State */}
                        {galleryImages.length === 0 && (
                          <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <p className="text-gray-600 font-medium mb-1">No images uploaded yet</p>
                            <p className="text-sm text-gray-500">
                              Upload images to start building the gallery
                            </p>
                          </div>
                        )}

                        {/* Image Grid */}
                        {galleryImages.length > 0 && (
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {galleryImages.map((image) => (
                              <div
                                key={image.id}
                                className="group relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-purple-400 transition-all cursor-pointer"
                              >
                                <img
                                  src={image.url}
                                  alt={image.name}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => window.open(image.url, "_blank")}
                                    className="opacity-0 group-hover:opacity-100 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-purple-100 transition"
                                  >
                                    <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteImage(image.id)}
                                    disabled={deletingImages}
                                    className="opacity-0 group-hover:opacity-100 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-red-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                                <div className="absolute top-2 left-2">
                                  <input
                                    type="checkbox"
                                    checked={image.selected}
                                    className="w-5 h-5 rounded border-2 border-white shadow-lg cursor-pointer accent-purple-700"
                                    onChange={() => handleImageSelect(image.id)}
                                  />
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                                  <p className="text-white text-sm truncate">{image.name}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  {/* Payments Section */}
                  {activeSection === "Payments" && (
                    <section className="space-y-6">
                      {/* Customer Spending Overview */}
                      <div>
                        <h3 className="text-xl font-bold mb-6">Customer Spending Overview</h3>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Total Spend Card */}
                          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                            <div className="flex flex-col items-center text-center">
                              <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                </svg>
                              </div>
                              <p className="text-sm font-medium text-gray-600 mb-2">Total Spend</p>
                              <p className="text-3xl font-bold text-gray-900">£{Number(paymentData.totalSpent).toFixed(2)}</p>
                            </div>

                            {/* Breakdown */}
                            <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
                              <div className="text-center">
                                <p className="text-xl font-bold text-gray-900">£{Number(paymentData.productAmount).toFixed(2)}</p>
                                <p className="text-sm text-gray-600 mt-1">Product spend total</p>
                              </div>
                              <div className="text-center border-x border-gray-200">
                                <p className="text-xl font-bold text-gray-900">£{Number(paymentData.serviceSpent).toFixed(2)}</p>
                                <p className="text-sm text-gray-600 mt-1">Service spend total</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xl font-bold text-gray-900">£{Number(paymentData.totalBookingsRefund).toFixed(2)}</p>
                                <p className="text-sm text-gray-600 mt-1">Total Refunds</p>
                              </div>
                            </div>
                          </div>

                          {/* Total Net Revenue Card */}
                          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm flex flex-col items-center justify-center text-center">
                            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <p className="text-sm font-medium text-gray-600 mb-2">Total Net Revenue</p>
                            <p className="text-3xl font-bold text-gray-900">£{(Number(paymentData.productAmount) + Number(paymentData.serviceSpent) - Number(paymentData.totalBookingsRefund)).toFixed(2)}</p>
                            <p className="text-sm text-gray-500 mt-2">(Product + Service - Refunds)</p>
                          </div>
                        </div>
                      </div>
                    </section>
                  )}
                </main>
              </section>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* History Drawer */}
      <PurchaseHistoryDrawer
        isOpen={drawerOpen}
        onClose={handleClosePurchaseDrawer}
        purchase={selectedPurchase}
        customerName={customer?.name}
        customerEmail={customer?.email}
      />

      {/* Allergy Modal */}
      <AllergyModal
        isOpen={allergyModalOpen}
        onClose={handleCloseAllergyModal}
        onSave={handleSaveAllergy}
        editData={editingAllergy}
        loading={savingAllergy}
      />

      {/* Patch Test Modal */}
      <PatchTestModal
        isOpen={patchTestModalOpen}
        onClose={handleClosePatchTestModal}
        onSave={handleSavePatchTest}
        onDelete={handleDeletePatchTest}
        editData={editingPatchTest}
        loading={savingPatchTest}
        deleting={deletingPatchTest}
      />

      {/* Notes Modal */}
      <NotesModal
        isOpen={notesModalOpen}
        onClose={handleCloseNotesModal}
        onSave={handleSaveNote}
        editData={editingNote}
        loading={savingNote}
      />

      {/* Edit Customer Modal */}
      <AddCustomerModal
        isOpen={editCustomerModalOpen}
        onClose={() => setEditCustomerModalOpen(false)}
        onSave={handleSaveCustomer}
        editData={customer}
      />

      {/* Payment Step Drawer */}
      {paymentStepOpen && selectedAppointment && (
        <PosPaymentStep
          selectedService={{
            id: selectedAppointment.id.toString(),
            name: selectedAppointment.title,
            duration: selectedAppointment.duration,
            price: selectedAppointment.price,
          }}
          selectedCustomer={customer?.name || ""}
          appointmentTime={selectedAppointment.dateLabel}
          tip={tip}
          setTip={setTip}
          goToFinalPayment={handleGoToFinalPayment}
          onClose={handleClosePaymentStep}
          mode="paynow"
        />
      )}
    </>
  );
}
